<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header (matching create page) -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="2" class="page-header-card">
            <v-card-title class="py-1 px-2">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">
                        Certificate Details
                      </h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        View and modify medical council certificate information
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Edit Toggle and Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Edit/Read-Only Toggle Button -->
                    <v-btn
                      :color="isEditMode ? 'warning' : 'primary'"
                      :outlined="!isEditMode"
                      @click="toggleEditMode"
                      class="mr-2"
                      small
                    >
                      <v-icon left>{{ isEditMode ? 'mdi-eye' : 'mdi-pencil' }}</v-icon>
                      {{ isEditMode ? 'Read Only' : 'Edit Mode' }}
                    </v-btn>
                    
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                      small
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Certificates
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Form Header -->
            <v-card-title class="form-header py-1 px-2">
              <h2 class="form-title">Certificate Information</h2>
              <v-spacer></v-spacer>
              
              <!-- Status Chips -->
              <div class="d-flex align-center">
                <v-chip 
                  :color="certificate.status ? 'success' : 'warning'" 
                  small
                  class="mr-2"
                >
                  <v-icon left x-small>
                    {{ certificate.status ? 'mdi-check-decagram' : 'mdi-clock-alert' }}
                  </v-icon>
                  {{ certificate.status ? 'Verified' : 'Pending' }}
                </v-chip>

                <v-chip 
                  :color="getExpiryColor(certificate)" 
                  small
                  class="mr-2"
                >
                  <v-icon left x-small>{{ getExpiryIcon(certificate) }}</v-icon>
                  {{ getExpiryLabel(certificate) }}
                </v-chip>

                <v-chip 
                  :color="isEditMode ? 'success' : 'warning'" 
                  :outlined="!isEditMode" 
                  x-small
                >
                  <v-icon left x-small>
                    {{ isEditMode ? 'mdi-pencil' : 'mdi-eye' }}
                  </v-icon>
                  {{ isEditMode ? 'Edit Mode' : 'Read Only Mode' }}
                </v-chip>
              </div>
            </v-card-title>

            <v-divider></v-divider>
            
            <!-- Loading State -->
            <v-overlay v-if="isLoading" absolute>
              <health-loader />
            </v-overlay>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2" v-if="!isLoading">
              <v-form ref="form" v-model="valid" lazy-validation>
                
                <!-- Basic Certificate Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-certificate</v-icon>
                    Certificate Information
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.user_id"
                        :items="doctors"
                        label="Doctor"
                        placeholder="Select doctor"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'Doctor is required']"
                        :error-messages="errors.user_id"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-doctor</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.council_name"
                        label="Medical Council Name"
                        placeholder="e.g., Maharashtra Medical Council"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'Council name is required']"
                        :error-messages="errors.council_name"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-domain</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.registration_number"
                        label="Registration Number"
                        placeholder="e.g., MMC12345"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'Registration number is required']"
                        :error-messages="errors.registration_number"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-numeric</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.registration_year"
                        label="Registration Year"
                        placeholder="e.g., 2020"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        type="number"
                        :min="1900"
                        :max="new Date().getFullYear() + 1"
                        :error-messages="errors.registration_year"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Date Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-calendar-range</v-icon>
                    Date Information
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.expiry_date"
                        label="Expiry Date"
                        placeholder="Select expiry date"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        type="date"
                        :error-messages="errors.expiry_date"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar-clock</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.renewal_date"
                        label="Last Renewal Date"
                        placeholder="Select last renewal date"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        type="date"
                        :error-messages="errors.renewal_date"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar-refresh</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Certificate Document Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-file-pdf-box</v-icon>
                    Certificate Document
                  </h3>
                  
                  <v-row>
                    <!-- Current Certificate Display -->
                    <v-col cols="12" md="6" v-if="form.certificate_path">
                      <div class="current-certificate">
                        <v-card outlined class="pa-3">
                          <div class="d-flex align-center justify-space-between">
                            <div class="d-flex align-center">
                              <v-icon color="error" class="mr-2">mdi-file-pdf-box</v-icon>
                              <div>
                                <div class="text-body-2 font-weight-medium">Current Certificate</div>
                                <div class="text-caption grey--text">PDF Document</div>
                              </div>
                            </div>
                            <div>
                              <v-btn
                                small
                                outlined
                                color="primary"
                                @click="viewCurrentCertificate"
                                class="mr-1"
                              >
                                <v-icon left x-small>mdi-eye</v-icon>
                                View
                              </v-btn>
                              <v-btn
                                v-if="isEditMode"
                                small
                                outlined
                                color="error"
                                @click="removeCertificate"
                              >
                                <v-icon left x-small>mdi-delete</v-icon>
                                Remove
                              </v-btn>
                            </div>
                          </div>
                        </v-card>
                      </div>
                    </v-col>

                    <!-- File Upload (Edit Mode Only) -->
                    <v-col cols="12" :md="form.certificate_path ? 6 : 12" v-if="isEditMode">
                      <v-file-input
                        v-model="newCertificateFile"
                        :label="form.certificate_path ? 'Replace Certificate Document' : 'Upload Certificate Document'"
                        placeholder="Choose PDF, JPG, or PNG file"
                        outlined
                        accept=".pdf,.jpg,.jpeg,.png"
                        :error-messages="errors.certificate_path"
                        class="professional-field"
                        hide-details="auto"
                        prepend-icon=""
                        show-size
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-file-upload</v-icon>
                        </template>
                      </v-file-input>
                      <div class="text-caption grey--text mt-1">
                        Maximum file size: 5MB. Supported formats: PDF, JPG, PNG
                      </div>
                    </v-col>

                    <!-- No Certificate Message -->
                    <v-col cols="12" v-else-if="!form.certificate_path">
                      <v-alert type="info" outlined class="ma-0">
                        <div class="d-flex align-center">
                          <v-icon class="mr-2">mdi-information</v-icon>
                          <span>No certificate document uploaded</span>
                        </div>
                      </v-alert>
                    </v-col>
                  </v-row>
                </div>

                <!-- Verification Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-check-decagram</v-icon>
                    Verification & Status
                  </h3>
                  
                  <v-row>
                    <!-- Verification Status (Admin Only) -->
                    <v-col cols="12" md="6" v-if="canVerify">
                      <v-switch
                        v-model="form.status"
                        label="Mark as Verified"
                        :disabled="!isEditMode"
                        :error-messages="errors.status"
                        color="success"
                        hide-details="auto"
                      ></v-switch>
                      <div class="text-caption grey--text mt-1">
                        Only administrators can verify certificates
                      </div>
                    </v-col>

                    <!-- Active Status -->
                    <v-col cols="12" md="6">
                      <v-switch
                        v-model="form.is_active"
                        label="Active Status"
                        :disabled="!isEditMode"
                        :error-messages="errors.is_active"
                        color="primary"
                        hide-details="auto"
                      ></v-switch>
                      <div class="text-caption grey--text mt-1">
                        Inactive certificates won't be visible to users
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Verification Details (Read-Only) -->
                  <v-row v-if="certificate.verified_at || certificate.verified_by">
                    <v-col cols="12" md="6">
                      <v-text-field
                        :value="formatDateTime(certificate.verified_at)"
                        label="Verified At"
                        outlined
                        readonly
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar-clock</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        :value="getVerifierName(certificate.verified_by)"
                        label="Verified By"
                        outlined
                        readonly
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account-check</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Action Buttons (Edit Mode Only) -->
                <div class="form-actions mt-4" v-if="isEditMode">
                  <v-row>
                    <v-col cols="12">
                      <div class="d-flex justify-space-between align-center">
                        <!-- Left Side: Danger Actions -->
                        <div class="d-flex">
                          <v-btn
                            outlined
                            color="error"
                            @click="deleteDialog = true"
                            :disabled="isSaving"
                          >
                            <v-icon left>mdi-delete</v-icon>
                            Delete Certificate
                          </v-btn>
                        </div>

                        <!-- Right Side: Save Actions -->
                        <div class="d-flex">
                          <v-btn
                            outlined
                            color="grey"
                            @click="resetForm"
                            :disabled="isSaving"
                            class="mr-2"
                          >
                            <v-icon left>mdi-refresh</v-icon>
                            Reset Changes
                          </v-btn>

                          <v-btn
                            color="primary"
                            @click="updateCertificate"
                            :loading="isSaving"
                            :disabled="!valid"
                          >
                            <v-icon left>mdi-content-save</v-icon>
                            Save Changes
                          </v-btn>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </div>

              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6 error--text">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text>
          <p>Are you sure you want to delete this certificate?</p>
          <v-alert type="warning" dense class="mt-3 mb-0">
            This action will soft delete the certificate. It can be restored later from the deleted certificates section.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteCertificate">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <FileViewer v-model="certificateDialog" :url="currentCertificateUrl" title="Certificate Document" />
  </v-main>
</template>

<script>
import axios from "@/axios";
import { FileViewer } from "@/components";

export default {
  name: "EditCouncilRegistrationCertificate",
  components: { FileViewer },
  data() {
    return {
      valid: false,
      isLoading: true,
      isSaving: false,
      isEditMode: false,
      deleteDialog: false,
      certificateDialog: false,
      currentCertificateUrl: null,
      newCertificateFile: null,
      doctors: [],
      
      certificate: {},
      originalForm: {},
      form: {
        user_id: null,
        council_name: '',
        registration_number: '',
        registration_year: null,
        expiry_date: null,
        renewal_date: null,
        certificate_path: null,
        status: false,
        is_active: true,
      },
    };
  },

  computed: {
    certificateId() {
      return this.$route.params.id;
    },

    canVerify() {
      return this.roleName === 'Super Admin' || this.roleName === 'Admin';
    },

    canEdit() {
      if (this.canVerify) return true;
      return this.certificate.user_id === this.user.id;
    }
  },

  async mounted() {
    await this.loadData();
  },

  methods: {
    async loadData() {
      this.isLoading = true;
      
      try {
        await Promise.all([
          this.loadCertificate(),
          this.loadDoctors()
        ]);
      } catch (error) {
        console.error("Error loading data:", error);
        this.$toast.error("Failed to load certificate data");
      } finally {
        this.isLoading = false;
      }
    },

    async loadCertificate() {
      const response = await axios.get(`/council_registration_certificates/${this.certificateId}`);
      this.certificate = response.data.data;
      
      // Format dates for form inputs
      this.form = {
        user_id: this.certificate.user_id,
        council_name: this.certificate.council_name || '',
        registration_number: this.certificate.registration_number || '',
        registration_year: this.certificate.registration_year,
        expiry_date: this.certificate.expiry_date ? this.formatDateForInput(this.certificate.expiry_date) : null,
        renewal_date: this.certificate.renewal_date ? this.formatDateForInput(this.certificate.renewal_date) : null,
        certificate_path: this.certificate.certificate_path,
        status: this.certificate.status || false,
        is_active: this.certificate.is_active !== undefined ? this.certificate.is_active : true,
      };

      // Store original form for reset
      this.originalForm = { ...this.form };
    },

    async loadDoctors() {
      try {
        const response = await axios.get('/users', {
          params: { role_name: 'Doctor' }
        });
        
        this.doctors = response.data.data.map(doctor => ({
          text: `${doctor.first_name} ${doctor.last_name} (${doctor.email})`,
          value: doctor.id,
        }));
      } catch (error) {
        console.error("Error loading doctors:", error);
      }
    },

    toggleEditMode() {
      if (!this.canEdit) {
        this.$toast.error("You don't have permission to edit this certificate");
        return;
      }
      
      this.isEditMode = !this.isEditMode;
      
      if (!this.isEditMode) {
        // Reset form when exiting edit mode
        this.resetForm();
      }
    },

    async updateCertificate() {
      if (!this.$refs.form.validate()) {
        this.$toast.error("Please fill in all required fields correctly");
        return;
      }

      this.isSaving = true;

      try {
        const formData = new FormData();
        
        // Add form fields
        Object.keys(this.form).forEach(key => {
          if (this.form[key] !== null && this.form[key] !== '') {
            formData.append(key, this.form[key]);
          }
        });

        // Add new certificate file if uploaded
        if (this.newCertificateFile) {
          formData.append('certificate_path', this.newCertificateFile);
        }

        await axios.post(`/council_registration_certificates/${this.certificateId}?_method=PUT`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        this.$toast.success("Certificate updated successfully!");
        
        // Reload data and exit edit mode
        await this.loadCertificate();
        this.isEditMode = false;
        this.newCertificateFile = null;

      } catch (error) {
        console.error("Error updating certificate:", error);
        
        if (error.response?.status === 422) {
          this.$toast.error("Please check the form data and try again");
        } else {
          this.$toast.error("Failed to update certificate");
        }
      } finally {
        this.isSaving = false;
      }
    },

    async deleteCertificate() {
      try {
        await axios.post(`/council_registration_certificates/delete/${this.certificateId}`);
        this.$toast.success("Certificate deleted successfully");
        this.deleteDialog = false;
        this.$router.push('/council_registration_certificates');
      } catch (error) {
        console.error("Error deleting certificate:", error);
        this.$toast.error("Failed to delete certificate");
      }
    },

    resetForm() {
      this.form = { ...this.originalForm };
      this.newCertificateFile = null;
      this.$refs.form.resetValidation();
    },

    viewCurrentCertificate() {
      if (this.form.certificate_path) {
        this.currentCertificateUrl = this.mediaUrl + '/' + this.form.certificate_path;
        this.certificateDialog = true;
      }
    },

    removeCertificate() {
      if (confirm('Remove current certificate document?')) {
        this.form.certificate_path = null;
      }
    },

    formatDateForInput(date) {
      if (!date) return null;
      return new Date(date).toISOString().split('T')[0];
    },

    formatDateTime(datetime) {
      if (!datetime) return 'N/A';
      return new Date(datetime).toLocaleString();
    },

    getVerifierName(verifier) {
      if (!verifier) return 'N/A';
      return `${verifier.first_name} ${verifier.last_name}`.trim() || verifier.email;
    },

    getExpiryColor(certificate) {
      if (!certificate.expiry_date) return 'grey';
      const expiryDate = new Date(certificate.expiry_date);
      const now = new Date();
      const threeMonthsFromNow = new Date();
      threeMonthsFromNow.setMonth(now.getMonth() + 3);

      if (expiryDate < now) return 'error';
      if (expiryDate <= threeMonthsFromNow) return 'warning';
      return 'success';
    },

    getExpiryIcon(certificate) {
      if (!certificate.expiry_date) return 'mdi-calendar-question';
      const expiryDate = new Date(certificate.expiry_date);
      const now = new Date();
      const threeMonthsFromNow = new Date();
      threeMonthsFromNow.setMonth(now.getMonth() + 3);

      if (expiryDate < now) return 'mdi-calendar-remove';
      if (expiryDate <= threeMonthsFromNow) return 'mdi-calendar-alert';
      return 'mdi-calendar-check';
    },

    getExpiryLabel(certificate) {
      if (!certificate.expiry_date) return 'No Expiry';
      const expiryDate = new Date(certificate.expiry_date);
      const now = new Date();
      const threeMonthsFromNow = new Date();
      threeMonthsFromNow.setMonth(now.getMonth() + 3);

      if (expiryDate < now) return 'Expired';
      if (expiryDate <= threeMonthsFromNow) return 'Expires Soon';
      return 'Valid';
    },
  },
};
</script>

<style scoped>
.page-header-card {
  border-radius: 12px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.page-subtitle {
  font-size: 0.875rem;
  opacity: 0.8;
}

.back-button {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
}

.form-card {
  border-radius: 12px;
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #e0e0e0;
}

.form-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #424242;
}

.form-content {
  background-color: #fafafa;
}

.form-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.section-title {
  color: #424242;
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.professional-field >>> .v-input__control {
  border-radius: 8px;
}

.professional-field >>> .v-text-field__details {
  padding-left: 0;
  padding-right: 0;
}

.form-actions {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.v-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
}

.current-certificate {
  margin-bottom: 16px;
}
</style>